import { useMemo } from 'react';
import { groupBy } from 'lodash';

import {
  AccountAssetDTO,
  AccountAssetSimulatedDTO,
  EquityCompensationAssetDTO,
  OtherAssetsAssetDTO,
  useEstatePlanningExplanationQuery,
} from '../../api/useEstatePlanningExplanationQuery';
import { combineQueryResults } from '../../../../../../CommonUtils/combineQueryResults';
import { useEstatePlanningExplanationOptionsQuery } from '../../api/useEstatePlanningExplanationOptionsQuery';
import { OptionType } from '../../../../../../CommonComponents/OPL/Select/BaseSelect/types';

export interface BeneficiariesData<T> {
  key: string;
  items: T[];
  allChecked: boolean;
}

const parseData = ([key, values]) => {
  return {
    key,
    items: values,
    allChecked: !values.find(({ IsChecked }) => IsChecked === false),
  };
};

export const useBeneficiaries = () => {
  const estatePlanningQueryResult = useEstatePlanningExplanationQuery();

  const optionsQueryResult = useEstatePlanningExplanationOptionsQuery();

  const data = useMemo(() => {
    if (!estatePlanningQueryResult.data) {
      return null;
    }

    if (!optionsQueryResult.data) {
      return null;
    }

    const { Current, Simulated } = estatePlanningQueryResult.data;

    const ownerDictionary = optionsQueryResult.data.OwnerOptions.reduce(
      (acc, { Name, FullName }) => {
        return {
          ...acc,
          [Name]: FullName,
        };
      },
      {},
    );

    const accountOwner = optionsQueryResult.data.BeneficiariesOptions.reduce(
      (acc: OptionType<string> | null, { Id, Name }) => {
        if (Number(Id)) {
          const owner = optionsQueryResult.data.OwnerOptions.find(
            ({ FullName }) => FullName === Name,
          );

          if (owner) {
            return { value: Id, label: owner.Name };
          }
        }

        return acc;
      },
      null,
    );

    const beneficiariesOptions = optionsQueryResult.data.BeneficiariesOptions.map(
      ({ Id, Name }) => {
        if (Number(Id)) {
          const owner = optionsQueryResult.data.OwnerOptions.find(
            ({ FullName }) => FullName === Name,
          );

          if (owner) {
            return { value: Id, label: owner.Name, isOwner: true };
          }
        }

        return {
          value: Id,
          label: Name,
        };
      },
    ) as OptionType<string>[];

    return {
      Current: {
        Accounts: Object.entries(groupBy(Current.Accounts, 'TypeName'))
          .map(parseData)
          .sort(({ key: keyA }, { key: keyB }) => keyA.localeCompare(keyB)) as BeneficiariesData<
          AccountAssetDTO
        >[],

        OtherAssets: Object.entries(groupBy(Current.OtherAssets, 'TypeName'))
          .map(parseData)
          .sort(({ key: keyA }, { key: keyB }) => keyA.localeCompare(keyB)) as BeneficiariesData<
          OtherAssetsAssetDTO
        >[],

        EquityCompensations: Object.entries(groupBy(Current.EquityCompensations, 'Name'))
          .map(parseData)
          .sort(({ key: keyA }, { key: keyB }) => keyA.localeCompare(keyB)) as BeneficiariesData<
          EquityCompensationAssetDTO
        >[],
      },

      Simulated: {
        Accounts: Object.entries(groupBy(Simulated.Accounts, 'TypeName'))
          .map(parseData)
          .sort(({ key: keyA }, { key: keyB }) => keyA.localeCompare(keyB)) as BeneficiariesData<
          AccountAssetSimulatedDTO
        >[],

        OtherAssets: Object.entries(groupBy(Simulated.OtherAssets, 'TypeName'))
          .map(parseData)
          .sort(({ key: keyA }, { key: keyB }) => keyA.localeCompare(keyB)) as BeneficiariesData<
          OtherAssetsAssetDTO
        >[],
      },

      beneficiariesOptions,

      accountOwner,

      ownerDictionary,

      ownerOptions: Object.entries(ownerDictionary).map(([name]) => ({
        value: name,
        label: name,
      })),

      trustOwner: optionsQueryResult.data.OwnerOptions.find(({ IsTrust }) => IsTrust),

      includedInEstatePlanOptions: optionsQueryResult.data.IncludedInEstatePlanOptions,

      transferMethodOptions: optionsQueryResult.data.TransferMethodOptions,
    };
  }, [estatePlanningQueryResult.data, optionsQueryResult.data]);

  return {
    ...combineQueryResults([estatePlanningQueryResult, optionsQueryResult]),
    data,
  };
};
